<ng-container *ngIf="!isLoading; else loadingTemplate">
  <div [ngClass]="isMobile ? 'mobile-header' : 'header'">
    <app-external-header
      style="width: 100%; z-index: 1000; position: relative;"
      [isInboxPresent]="true"
      [isCompanyProfile]="true"
      [isProfile]="false"
      [isMobile]="isMobile"
      [showButtons]="false"
      [showProfilePicture]="true"
    ></app-external-header>
  </div>
  <div class="dashboard-wrapper">
    <div style="display: flex; gap: 1rem; padding-bottom: 1rem;">
      <img
        class="applicant-image"
        loading="lazy"
        [src]="companyData.logoUrl"
        alt="Profile Picture"
      />
      <div class="workspace-name">{{ companyData.name }}</div>
    </div>
    <h1 class="welcome-text">
      {{ 'dashboard.welcome' | translate:{name: companyData.industry} }}
    </h1>

    <!-- Welcome Banner -->
    <div class="banner">
      <div class="banner-content">
        <p class="cta-description">
          {{ 'dashboard.ctaTitle' | translate }}<br/>
          <span class="sub-text">{{ 'dashboard.ctaSubtitle' | translate }}</span>
        </p>
        <div class="create-offer-button">
          <app-button
            (click)="createJobPost()"
            [label]="'dashboard.postButton' | translate"
            [showLeftIcon]="true"
            [leftIcon]="'assets/Left-icon.png'"
            [size]="ButtonSize.LARGE"
            [state]="ButtonState.REGULAR"
            [type]="ButtonType.PRIMARY">
          </app-button>
        </div>
      </div>

      <ng-container *ngIf="!isMobile">

        <div class="banner-image">
          <img src="../../../../../assets/experience.png" alt="Dashboard preview"/>
          <img class="user-avatar" src="../../../../../assets/happywoman.png" alt="User"/>
        </div>
      </ng-container>

    </div>

    <!-- Jobs and Applications -->
    <div class="cards-container">

      <!-- Left column: Active Jobs + Interviews Calendar -->
      <div class="left-column">

        <!-- Active Jobs -->
        <div class="card jobs-card">
          <div class="card-header with-link">
            <div class="card-title">
              {{ jobOffers.length }} {{ 'dashboard.activeJobs' | translate }}
            </div>
            <a
              class="view-all-link"
              [routerLink]="'/jobs'"
              [state]="{ jobs: jobOffers }"
            >
              {{ 'dashboard.viewAll' | translate }}
            </a>
          </div>

          <div class="section-label">{{ 'dashboard.latestJobs' | translate }}</div>

          <ng-container *ngIf="jobOffers.length > 0; else noJobsTemplate">
            <a class="job-entry" *ngFor="let job of jobOffers | slice:0:5" [routerLink]="['/jobs', job.id]">
              <div class="job-title">{{ job.title }}</div>
              <div class="job-date">{{ getTimeAgo(job.createdAt) }}</div>
            </a>
          </ng-container>

          <ng-template #noJobsTemplate>
            <div class="empty-message">
              {{ 'dashboard.noJobs' | translate }}
            </div>
          </ng-template>
        </div>

        <!-- Interviews Calendar -->
        <div class="card interviews-calendar-card">
          <div class="card-header">
            <div class="card-title">{{ 'dashboard.interviewsCalendar' | translate }}</div>
          </div>

          <div *ngIf="isCalendarLoading" class="calendar-spinner">
            <mat-spinner [diameter]="32"></mat-spinner>
          </div>

          <div *ngIf="!isCalendarLoading" class="interviews-calendar">
            <div *ngFor="let day of calendarDays" class="day-group">
              <div class="day-header">
                <span class="day-label">{{ day.label }}</span>
                <span class="day-date">{{ day.dateLabel }}</span>
              </div>

              <ng-container *ngIf="day.interviews.length > 0; else noMeetingsTemplate">
                <div *ngFor="let interview of day.interviews" class="interview-card" (click)="navigateToInterview(interview.applicationId)">
                  <div class="interview-bar"></div>
                  <div class="interview-body">
                    <div class="interview-header">
                      <div class="interview-title">{{ interview.title }}</div>
                      <span class="interview-status" [ngClass]="'status-' + interview.status?.toLowerCase()">
                        {{ 'interviews.status.' + interview.status?.toLowerCase() | translate }}
                      </span>
                    </div>
                    <div class="interview-time">
                      <lucide-icon [img]="Clock" [size]="14"></lucide-icon>
                      {{ formatTime(interview.startTime) }} → {{ formatTime(interview.endTime) }}
                      <span class="duration">({{ getDuration(interview.startTime, interview.endTime) }})</span>
                    </div>
                    <div class="guests-row">
                      <div class="guest-avatars">
                        <div
                          *ngFor="let guest of interview.guests | slice:0:4"
                          class="guest-avatar"
                          [matTooltip]="guest.guestName"
                          matTooltipPosition="above"
                        >{{ getInitials(guest.guestName) }}</div>
                      </div>
                      <span class="guests-label">{{ interview.guests.length }} {{ 'dashboard.guestsInvited' | translate }}</span>
                    </div>
                  </div>
                  <lucide-icon class="interview-chevron" [img]="ChevronRight" [size]="18"></lucide-icon>
                </div>
              </ng-container>

              <ng-template #noMeetingsTemplate>
                <div class="no-meetings">
                  <lucide-icon [img]="CalendarX2" [size]="16"></lucide-icon>
                  <span>{{ 'dashboard.noMeetings' | translate }}</span>
                </div>
              </ng-template>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column: Recent Applications -->
      <div class="card apps-card">
        <div class="card-header">
          <div class="card-title">
            {{ applications.length }} {{ 'dashboard.recentApplications' | translate }}
          </div>
        </div>

        <div class="section-label">{{ 'dashboard.latestApplications' | translate }}</div>

        <ng-container *ngIf="applications.length > 0; else noAppsTemplate">
          <div class="applicant-list">
            <div class="applicant" *ngFor="let applicant of applications | slice:0:5">
              <img
                class="applicant-image"
                loading="lazy"
                [src]="applicant.applicantProfileImageUrl"
                alt="Profile Picture"
              />
              <div class="applicant-info">
                <span class="name clickable-text" (click)="navigateToProfile(applicant.userId)">
                  {{ applicant.applicantName }}
                </span>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #noAppsTemplate>
          <div class="empty-message">
            {{ 'dashboard.noApplications' | translate }}
          </div>
        </ng-template>
      </div>

    </div>

  </div>
</ng-container>

<div class="footer-class footer-container">
  <app-footer></app-footer>
</div>

<ng-template #loadingTemplate>
  <app-load-mask></app-load-mask>
</ng-template>
